import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import ParkCard from "@/components/ParkCard";
import { ParkCardSkeletonGrid } from "@/components/Skeletons";
import { api } from "@/lib/api";
import { ArrowRight, Sparkles, ShieldCheck, Zap } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/de09e536-c651-44e7-956b-62aa8a2b9aa3/images/1922d133eb6be0047a299bff40eea0604605a1e5c83560a08caff750e6af1831.png";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState(null);
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    api.searchParks({ sort: "rated", limit: 6 }).then((d) => setFeatured(d.results));
    api.stats().then(setStats);
    api.regions().then((rs) => setRegions(rs.slice(0, 8)));
  }, []);

  return (
    <div className="App">
      <div className="relative bg-gradient-to-br from-forest-dark via-forest to-forest-light">
        <Nav transparent />
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMG}
            alt="UK landscape"
            className="w-full h-[640px] object-cover opacity-90"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-forest/40" />
        </div>

        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24 text-white">
          <div className="max-w-3xl animate-fade-up">
            <span className="label-eyebrow text-white/80">The Skyscanner of UK caravan holidays</span>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] tracking-tight" data-testid="hero-title">
              Compare every<br />UK caravan park.<br /><span className="text-terracotta">In one search.</span>
            </h1>
            <p className="mt-4 text-white/85 max-w-xl text-lg">
              Haven, Parkdean, Butlins, Hoseasons & more — real prices for your dates, sorted cheapest first. No logins, no fluff.
            </p>
          </div>

          <div className="mt-10 max-w-5xl">
            <SearchBar />
          </div>

          {stats && (
            <div className="mt-6 flex flex-wrap gap-6 text-white/85 text-sm" data-testid="hero-stats">
              <span><strong className="text-white text-lg">{stats.parks}</strong> parks</span>
              <span><strong className="text-white text-lg">{stats.operators}</strong> operators</span>
              <span><strong className="text-white text-lg">{stats.regions}</strong> UK regions</span>
              <span className="hidden sm:inline">·</span>
              <span>Live pricing updated hourly</span>
            </div>
          )}
        </section>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { Icon: Zap, title: "Instant compare", body: "Real prices for your exact dates across every major UK operator." },
            { Icon: ShieldCheck, title: "Unbiased rankings", body: "Sort by cheapest, best-rated or closest to the beach — not by who paid." },
            { Icon: Sparkles, title: "All in one place", body: "From Haven flagships to hidden indie parks in the Lakes & Highlands." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="bg-white border border-border rounded-2xl p-6">
              <Icon className="w-6 h-6 text-forest" />
              <h3 className="font-display font-semibold text-lg mt-3">{title}</h3>
              <p className="text-ink/70 text-sm leading-relaxed mt-1">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="label-eyebrow">Top rated right now</span>
            <h2 className="font-display font-bold text-3xl mt-1">Most loved parks</h2>
          </div>
          <Link to="/search?sort=rated" className="btn-ghost text-sm" data-testid="home-see-all-top">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {!featured ? <ParkCardSkeletonGrid count={6} /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {featured.map((p) => <ParkCard key={p.slug} park={p} />)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="label-eyebrow">By region</span>
            <h2 className="font-display font-bold text-3xl mt-1">Browse UK regions</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {regions.map((r) => (
            <Link
              key={r.slug}
              to={`/region/${r.slug}`}
              className="bg-white border border-border rounded-xl p-4 hover:border-forest hover:shadow-md transition-all"
              data-testid={`region-card-${r.slug}`}
            >
              <div className="font-display font-semibold text-lg">{r.name}</div>
              <div className="text-xs text-ink/60 mt-1">{r.count} parks</div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
